import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  useTheme,
  Tab,
  Tabs,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Switch,
  Button,
  IconButton,
  Avatar,
  Autocomplete,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Edit, Delete, Add, PersonOff } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import {
  getFacilityById,
  assignOwner,
  unassignOwner,
  toggleBookable,
  updateFacilityStatus,
  getActivities,
  createActivity,
  updateActivity,
  deleteActivity,
  getSlotSchedules,
  upsertSlotSchedule,
  getManagers,
  getFacilityRevenue,
  searchAthletes,
} from "service/admin/facilityService";
import { getBookings } from "service/admin/bookingService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FacilityForm from "./FacilityForm";
import moment from "moment";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const TabPanel = ({ value, index, children }) => (
  <div hidden={value !== index}>
    {value === index && <Box pt={2}>{children}</Box>}
  </div>
);

const statusChip = (status) => {
  const map = {
    active: { label: "Active", color: "success" },
    pending: { label: "Pending", color: "warning" },
    suspended: { label: "Suspended", color: "error" },
    confirmed: { label: "Confirmed", color: "success" },
    cancelled: { label: "Cancelled", color: "error" },
    completed: { label: "Completed", color: "info" },
  };
  const cfg = map[status] || { label: status || "-", color: "default" };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const defaultSlot = () => ({
  is_enabled: false,
  open_time: "",
  close_time: "",
  slot_duration: 60,
  price: "",
  peak_price: "",
  peak_start: "",
  peak_end: "",
  max_bookings: "",
});

const ActivityDialog = ({ open, onClose, facilityId, activity, onSuccess }) => {
  const isEdit = Boolean(activity);
  const [values, setValues] = useState({
    name: "",
    description: "",
    duration: 60,
    base_price: "",
    peak_price: "",
    has_coaching: false,
    max_participants: "",
    status: "active",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (activity) {
      setValues({
        name: activity.name || "",
        description: activity.description || "",
        duration: activity.duration || 60,
        base_price: activity.base_price || "",
        peak_price: activity.peak_price || "",
        has_coaching: activity.has_coaching || false,
        max_participants: activity.max_participants || "",
        status: activity.status || "active",
      });
    } else {
      setValues({
        name: "",
        description: "",
        duration: 60,
        base_price: "",
        peak_price: "",
        has_coaching: false,
        max_participants: "",
        status: "active",
      });
    }
  }, [activity, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async () => {
    if (!values.name) {
      toast.error("Activity name is required.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: values.name,
        description: values.description || undefined,
        duration: Number(values.duration),
        base_price: values.base_price !== "" ? Number(values.base_price) : undefined,
        peak_price: values.peak_price !== "" ? Number(values.peak_price) : undefined,
        has_coaching: values.has_coaching,
        max_participants:
          values.max_participants !== "" ? Number(values.max_participants) : undefined,
        status: values.status,
      };
      if (isEdit) {
        await updateActivity(facilityId, activity.id, payload);
        toast.success("Activity updated.");
      } else {
        await createActivity(facilityId, payload);
        toast.success("Activity created.");
      }
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save activity.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Activity" : "Add Activity"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Name *" name="name"
              color="secondary" value={values.name} onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Description" name="description"
              color="secondary" multiline rows={2} value={values.description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Duration (min)" name="duration"
              type="number" color="secondary" value={values.duration} onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Max Participants" name="max_participants"
              type="number" color="secondary" value={values.max_participants}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Base Price" name="base_price"
              type="number" color="secondary" value={values.base_price} onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Peak Price" name="peak_price"
              type="number" color="secondary" value={values.peak_price} onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel color="secondary">Status</InputLabel>
              <Select
                color="secondary" label="Status" name="status"
                value={values.status} onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch color="secondary" name="has_coaching" checked={values.has_coaching}
                  onChange={handleChange} />
              }
              label="Has Coaching"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="secondary" variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FacilityDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [facility, setFacility] = useState(null);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const [selectedActivityForSlots, setSelectedActivityForSlots] = useState("");
  const [slots, setSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const [athleteSearch, setAthleteSearch] = useState("");
  const [athleteOptions, setAthleteOptions] = useState([]);
  const [athleteSearchLoading, setAthleteSearchLoading] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const searchTimerRef = useRef(null);

  const fetchFacility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFacilityById(id);
      const payload = response.data.data;
      setFacility(payload.facility || payload);
      setSports(payload.sports || []);
    } catch (error) {
      toast.error("Failed to load facility.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActivities = useCallback(async () => {
    try {
      setActivitiesLoading(true);
      const response = await getActivities(id);
      const actData = response.data.data;
      setActivities(Array.isArray(actData) ? actData : actData.data || []);
    } catch {
      toast.error("Failed to load activities.");
    } finally {
      setActivitiesLoading(false);
    }
  }, [id]);

  const fetchSlots = useCallback(async () => {
    try {
      setSlotsLoading(true);
      const response = await getSlotSchedules(id);
      const slotRaw = response.data.data;
      const data = Array.isArray(slotRaw) ? slotRaw : slotRaw.data || [];
      const slotMap = {};
      DAYS.forEach((d) => { slotMap[d] = defaultSlot(); });
      data.forEach((s) => {
        if (s.day_of_week) {
          slotMap[s.day_of_week] = {
            id: s.id,
            is_enabled: s.is_enabled || false,
            open_time: s.open_time || "",
            close_time: s.close_time || "",
            slot_duration: s.slot_duration || 60,
            price: s.price || "",
            peak_price: s.peak_price || "",
            peak_start: s.peak_start || "",
            peak_end: s.peak_end || "",
            max_bookings: s.max_bookings || "",
          };
        }
      });
      setSlots(slotMap);
    } catch {
      toast.error("Failed to load slot schedules.");
    } finally {
      setSlotsLoading(false);
    }
  }, [id]);

  const fetchBookings = useCallback(async () => {
    try {
      setBookingsLoading(true);
      const response = await getBookings({ facility_id: id, limit: 100 });
      const data = response.data.data;
      setBookings(Array.isArray(data) ? data : data.data || []);
    } catch {
      toast.error("Failed to load bookings.");
    } finally {
      setBookingsLoading(false);
    }
  }, [id]);

  const fetchManagers = useCallback(async () => {
    try {
      setManagersLoading(true);
      const response = await getManagers(id);
      const mgData = response.data.data;
      setManagers(Array.isArray(mgData) ? mgData : mgData.data || []);
    } catch {
      toast.error("Failed to load managers.");
    } finally {
      setManagersLoading(false);
    }
  }, [id]);

  const fetchRevenue = useCallback(async () => {
    try {
      setRevenueLoading(true);
      const response = await getFacilityRevenue(id);
      setRevenue(response.data.data);
    } catch {
      toast.error("Failed to load revenue.");
    } finally {
      setRevenueLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFacility();
  }, [fetchFacility]);

  useEffect(() => {
    if (tabValue === 1) fetchActivities();
    else if (tabValue === 2) { fetchSlots(); fetchActivities(); }
    else if (tabValue === 3) fetchBookings();
    else if (tabValue === 4) fetchManagers();
    else if (tabValue === 5) fetchRevenue();
  }, [tabValue, fetchActivities, fetchSlots, fetchBookings, fetchManagers, fetchRevenue]);

  const handleAthleteSearchInput = (val) => {
    setAthleteSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val) { setAthleteOptions([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        setAthleteSearchLoading(true);
        const response = await searchAthletes(val);
        const payload = response.data.data;
        const data = Array.isArray(payload) ? payload : payload.data || [];
        setAthleteOptions(data);
      } catch {
        setAthleteOptions([]);
      } finally {
        setAthleteSearchLoading(false);
      }
    }, 400);
  };

  const handleAssignOwner = async () => {
    if (!selectedAthlete) return;
    try {
      await assignOwner(id, selectedAthlete.id);
      toast.success("Owner assigned.");
      setSelectedAthlete(null);
      setAthleteSearch("");
      fetchFacility();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign owner.");
    }
  };

  const handleUnassignOwner = async () => {
    if (!window.confirm("Remove the current owner?")) return;
    try {
      await unassignOwner(id);
      toast.success("Owner removed.");
      fetchFacility();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove owner.");
    }
  };

  const handleToggleBookable = async (val) => {
    try {
      await toggleBookable(id, val);
      setFacility((prev) => ({ ...prev, is_bookable: val }));
      toast.success(`Facility is now ${val ? "bookable" : "listing only"}.`);
    } catch {
      toast.error("Failed to update bookable status.");
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateFacilityStatus(id, status);
      setFacility((prev) => ({ ...prev, registration_status: status }));
      toast.success("Status updated.");
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await deleteActivity(id, activityId);
      toast.success("Activity deleted.");
      fetchActivities();
    } catch {
      toast.error("Failed to delete activity.");
    }
  };

  const handleSlotChange = (day, field, value) => {
    setSlots((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const handleSaveSlots = async () => {
    try {
      const fmtTime = (t) => {
        if (!t) return null;
        // type="time" gives HH:MM — append seconds for MySQL
        return t.length === 5 ? `${t}:00` : t;
      };
      const payload = DAYS.map((day) => ({
        day_of_week: day,
        activity_id: selectedActivityForSlots || null,
        is_enabled: slots[day].is_enabled || false,
        open_time: fmtTime(slots[day].open_time),
        close_time: fmtTime(slots[day].close_time),
        slot_duration: slots[day].slot_duration ? Number(slots[day].slot_duration) : 60,
        price: slots[day].price !== "" && slots[day].price != null ? Number(slots[day].price) : null,
        peak_price: slots[day].peak_price !== "" && slots[day].peak_price != null ? Number(slots[day].peak_price) : null,
        peak_start: fmtTime(slots[day].peak_start),
        peak_end: fmtTime(slots[day].peak_end),
        max_bookings: slots[day].max_bookings !== "" && slots[day].max_bookings != null ? Number(slots[day].max_bookings) : 1,
      }));
      await upsertSlotSchedule(id, { schedules: payload });
      toast.success("Slot schedules saved.");
      fetchSlots();
    } catch {
      toast.error("Failed to save slot schedules.");
    }
  };

  const bookingColumns = [
    { field: "booking_ref", headerName: "BOOKING REF", flex: 1 },
    {
      field: "athlete_name",
      headerName: "ATHLETE",
      flex: 1,
      renderCell: (params) =>
        params.row.athlete?.full_name || params.value || "-",
    },
    {
      field: "activity_name",
      headerName: "ACTIVITY",
      flex: 1,
      renderCell: (params) =>
        params.row.activity?.name || params.value || "-",
    },
    {
      field: "booking_date",
      headerName: "DATE",
      flex: 1,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY") : "-",
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      flex: 0.8,
      renderCell: (params) =>
        params.value ? `₹${params.value}` : "-",
    },
    {
      field: "status",
      headerName: "STATUS",
      flex: 0.8,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "payment_status",
      headerName: "PAYMENT",
      flex: 0.8,
      renderCell: (params) => statusChip(params.value),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!facility) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
      <Typography color="error">Failed to load facility. Please try refreshing the page.</Typography>
    </Box>
  );

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={2}>
          <Header
            title={facility.name || "Facility Detail"}
            subTitle={facility.category?.replace(/_/g, " ").toUpperCase() || ""}
          />
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Edit />}
            onClick={() => setEditOpen(true)}
          >
            Edit
          </Button>
        </FlexBetween>

        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Info" />
          <Tab label="Activities" />
          <Tab label="Slot Schedules" />
          <Tab label="Bookings" />
          <Tab label="Managers" />
          <Tab label="Revenue" />
        </Tabs>

        {/* TAB 1 — INFO */}
        <TabPanel value={tabValue} index={0}>
          {(() => {
            const BUCKET = "https://storage.googleapis.com/np-athletify-core/";
            const images = (() => {
              try {
                const raw = facility.images;
                if (!raw) return [];
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
              } catch { return []; }
            })();
            const weekdays = (() => {
              try {
                const raw = facility.weekdays;
                if (!raw) return [];
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
              } catch { return []; }
            })();
            const amenities = (() => {
              try {
                const raw = facility.amenities;
                if (!raw) return [];
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
              } catch { return []; }
            })();
            const bankDetails = (() => {
              try {
                const raw = facility.bank_details;
                if (!raw) return null;
                return typeof raw === "string" ? JSON.parse(raw) : raw;
              } catch { return null; }
            })();

            return (
              <Grid container spacing={3}>
                {/* Images */}
                {images.length > 0 && (
                  <Grid item xs={12}>
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                          Images
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {images.map((img, i) => {
                            const url = img && String(img).startsWith("http") ? img : `${BUCKET}${img}`;
                            return (
                              <Box
                                key={i}
                                component="img"
                                src={url}
                                alt={`facility-${i}`}
                                sx={{ width: 140, height: 100, objectFit: "cover", borderRadius: 1, flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Left column — Facility Info */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ backgroundColor: theme.palette.background.alt, mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                        Facility Info
                      </Typography>
                      <Box mt={1} display="flex" gap={1} flexWrap="wrap" mb={2}>
                        {statusChip(facility.registration_status)}
                        {facility.is_bookable ? <Chip label="Bookable" size="small" color="secondary" /> : null}
                        {facility.is_strikie_owned ? <Chip label="Strikie Owned" size="small" color="primary" /> : null}
                      </Box>
                      {[
                        ["Email", facility.email],
                        ["Phone", facility.phone],
                        ["Address", facility.address],
                        ["Category", facility.category?.replace(/_/g, " ")],
                        ["About", facility.about],
                        ["Web URL", facility.web_url],
                        ["Commission", facility.platform_commission_pct != null ? `${facility.platform_commission_pct}%` : null],
                      ].map(([label, value]) =>
                        value ? (
                          <Box key={label} mb={1}>
                            <Typography variant="caption" color={theme.palette.secondary[300]}>{label}</Typography>
                            <Typography color={theme.palette.secondary[100]}>{value}</Typography>
                          </Box>
                        ) : null
                      )}
                    </CardContent>
                  </Card>

                  {/* Sports Available */}
                  {sports.length > 0 && (
                    <Card sx={{ backgroundColor: theme.palette.background.alt, mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                          Sports Available
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1.5} mt={1}>
                          {sports.map((sport) => (
                            <Box key={sport.id} display="flex" alignItems="center" gap={0.75}
                              sx={{ width: "calc(50% - 6px)" }}>
                              {sport.icon && (
                                <Box
                                  component="img"
                                  src={sport.icon.startsWith("http") ? sport.icon : `${BUCKET}${sport.icon}`}
                                  alt={sport.title}
                                  sx={{ width: 22, height: 22, objectFit: "contain" }}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              )}
                              <Typography color={theme.palette.secondary[100]} variant="body2">
                                {sport.title}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Operating Hours */}
                  {weekdays.length > 0 && (
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                          Operating Hours
                        </Typography>
                        {weekdays.map((item, i) => (
                          <Box key={i} display="flex" gap={2} mb={0.5}>
                            <Typography color={theme.palette.secondary[300]} variant="body2"
                              sx={{ minWidth: 90 }}>
                              {item.weekday}
                            </Typography>
                            <Typography color={theme.palette.secondary[100]} variant="body2">
                              {item.other ? item.other : `${item["open-time"] || ""} – ${item["close-time"] || ""}`}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                {/* Right column — Controls, Owner, Amenities */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ backgroundColor: theme.palette.background.alt, mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                        Controls
                      </Typography>
                      <Box mb={2}>
                        <FormControlLabel
                          control={
                            <Switch color="secondary" checked={!!facility.is_bookable}
                              onChange={(e) => handleToggleBookable(e.target.checked)} />
                          }
                          label="Bookable"
                        />
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel color="secondary">Registration Status</InputLabel>
                        <Select color="secondary" label="Registration Status"
                          value={facility.registration_status || "pending"}
                          onChange={(e) => handleStatusChange(e.target.value)}>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="suspended">Suspended</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>

                  <Card sx={{ backgroundColor: theme.palette.background.alt, mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                        Owner
                      </Typography>
                      {facility.owner_id ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar src={facility.owner_profile_dp} alt={facility.owner_full_name}
                            sx={{ width: 48, height: 48 }} />
                          <Box flex={1}>
                            <Typography color={theme.palette.secondary[100]} fontWeight="bold">
                              {facility.owner_full_name}
                            </Typography>
                            <Typography variant="caption" color={theme.palette.secondary[300]}>
                              {facility.owner_email || facility.owner_phone_no}
                            </Typography>
                          </Box>
                          <IconButton color="error" size="small" onClick={handleUnassignOwner} title="Remove owner">
                            <PersonOff />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color={theme.palette.secondary[300]} mb={2}>
                            No owner assigned
                          </Typography>
                          <Autocomplete
                            options={athleteOptions}
                            getOptionLabel={(o) => `${o.full_name || ""} (@${o.username || ""}) ${o.phone_no || ""}`}
                            loading={athleteSearchLoading}
                            inputValue={athleteSearch}
                            onInputChange={(_, val) => handleAthleteSearchInput(val)}
                            value={selectedAthlete}
                            onChange={(_, val) => setSelectedAthlete(val)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" color="secondary" label="Search Athlete"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>{athleteSearchLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>
                                  ),
                                }} />
                            )}
                            sx={{ mb: 1 }}
                          />
                          <Button size="small" variant="contained" color="secondary"
                            disabled={!selectedAthlete} onClick={handleAssignOwner}>
                            Assign Owner
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Amenities */}
                  {amenities.length > 0 && (
                    <Card sx={{ backgroundColor: theme.palette.background.alt, mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                          Amenities
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {amenities.map((item, i) => (
                            <Box key={i} display="flex" alignItems="center" gap={0.5}
                              sx={{ width: "calc(50% - 4px)" }}>
                              <Typography
                                sx={{ fontSize: 14, color: item.availability === 1 ? "#4caf50" : "#f44336" }}>
                                {item.availability === 1 ? "✓" : "✗"}
                              </Typography>
                              <Typography color={theme.palette.secondary[100]} variant="body2">
                                {item.amenity_title}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Bank Details */}
                  {bankDetails && (
                    <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[200]} mb={1}>
                          Bank Details
                        </Typography>
                        {[
                          ["Account Holder", bankDetails.account_holder],
                          ["Account Number", bankDetails.account_number],
                          ["IFSC", bankDetails.ifsc],
                          ["Bank Name", bankDetails.bank_name],
                          ["UPI ID", bankDetails.upi_id],
                        ].map(([label, value]) =>
                          value ? (
                            <Box key={label} mb={1}>
                              <Typography variant="caption" color={theme.palette.secondary[300]}>{label}</Typography>
                              <Typography color={theme.palette.secondary[100]}>{value}</Typography>
                            </Box>
                          ) : null
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>
            );
          })()}
        </TabPanel>

        {/* TAB 2 — ACTIVITIES */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => { setSelectedActivity(null); setActivityDialogOpen(true); }}
            >
              Add Activity
            </Button>
          </Box>
          {activitiesLoading ? (
            <CircularProgress color="secondary" />
          ) : (
            <Grid container spacing={2}>
              {activities.map((act) => (
                <Grid item xs={12} sm={6} md={4} key={act.id}>
                  <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                    <CardContent>
                      <FlexBetween>
                        <Typography variant="h6" fontWeight="bold" color={theme.palette.secondary[100]}>
                          {act.name}
                        </Typography>
                        <Box>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => { setSelectedActivity(act); setActivityDialogOpen(true); }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteActivity(act.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </FlexBetween>
                      <Typography variant="caption" color={theme.palette.secondary[300]}>
                        {act.duration} min
                      </Typography>
                      <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                        {act.base_price && <Chip label={`Base: ₹${act.base_price}`} size="small" />}
                        {act.peak_price && <Chip label={`Peak: ₹${act.peak_price}`} size="small" />}
                        {act.has_coaching && <Chip label="Coaching" size="small" color="secondary" />}
                        {act.max_participants && <Chip label={`Max: ${act.max_participants}`} size="small" />}
                        {statusChip(act.status)}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {activities.length === 0 && (
                <Grid item xs={12}>
                  <Typography color={theme.palette.secondary[300]}>No activities found.</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </TabPanel>

        {/* TAB 3 — SLOT SCHEDULES */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel color="secondary">Select Activity</InputLabel>
              <Select
                color="secondary"
                label="Select Activity"
                value={selectedActivityForSlots}
                onChange={(e) => setSelectedActivityForSlots(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {activities.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" color="secondary" onClick={handleSaveSlots}>
              Save All Slots
            </Button>
          </Box>
          {slotsLoading ? (
            <CircularProgress color="secondary" />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Day</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Enabled</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Open</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Close</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Duration (min)</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Price</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Peak Price</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Peak Start</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Peak End</TableCell>
                    <TableCell sx={{ color: theme.palette.secondary[100] }}>Max Bookings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DAYS.map((day) => {
                    const s = slots[day] || defaultSlot();
                    return (
                      <TableRow key={day}>
                        <TableCell sx={{ color: theme.palette.secondary[200], textTransform: "capitalize" }}>
                          {day}
                        </TableCell>
                        <TableCell>
                          <Switch
                            color="secondary"
                            size="small"
                            checked={!!s.is_enabled}
                            onChange={(e) => handleSlotChange(day, "is_enabled", e.target.checked)}
                          />
                        </TableCell>
                        {[
                          { f: "open_time", type: "time", width: 110 },
                          { f: "close_time", type: "time", width: 110 },
                          { f: "slot_duration", type: "number", width: 70 },
                          { f: "price", type: "number", width: 80 },
                          { f: "peak_price", type: "number", width: 90 },
                          { f: "peak_start", type: "time", width: 110 },
                          { f: "peak_end", type: "time", width: 110 },
                          { f: "max_bookings", type: "number", width: 70 },
                        ].map(({ f, type, width }) => (
                          <TableCell key={f}>
                            <TextField
                              size="small"
                              variant="standard"
                              color="secondary"
                              type={type}
                              value={s[f] ?? ""}
                              onChange={(e) => handleSlotChange(day, f, e.target.value)}
                              sx={{ width }}
                              disabled={!s.is_enabled}
                              InputLabelProps={type === "time" ? { shrink: true } : undefined}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </TabPanel>

        {/* TAB 4 — BOOKINGS */}
        <TabPanel value={tabValue} index={3}>
          <Box
            height="60vh"
            sx={{
              "& .MuiDataGrid-root": { border: "none" },
              "& .MuiDataGrid-cell": { borderBottom: "none" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.primary.light,
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
              },
            }}
          >
            <DataGrid
              loading={bookingsLoading}
              getRowId={(row) => row.id}
              rows={bookings}
              columns={bookingColumns}
              rowsPerPageOptions={[25, 50]}
              pageSize={25}
              sx={{
                "& .MuiCircularProgress-circle": { color: theme.palette.secondary[300] },
              }}
            />
          </Box>
        </TabPanel>

        {/* TAB 5 — MANAGERS */}
        <TabPanel value={tabValue} index={4}>
          {managersLoading ? (
            <CircularProgress color="secondary" />
          ) : managers.length === 0 ? (
            <Typography color={theme.palette.secondary[300]}>No managers found.</Typography>
          ) : (
            <Grid container spacing={2}>
              {managers.map((mgr) => (
                <Grid item xs={12} sm={6} md={4} key={mgr.id}>
                  <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Avatar src={mgr.profile_dp} alt={mgr.full_name} />
                        <Box>
                          <Typography color={theme.palette.secondary[100]} fontWeight="bold">
                            {mgr.full_name}
                          </Typography>
                          <Typography variant="caption" color={theme.palette.secondary[300]}>
                            @{mgr.username} {mgr.phone_no ? `· ${mgr.phone_no}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                      {mgr.permissions && (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(Array.isArray(mgr.permissions)
                            ? mgr.permissions
                            : Object.keys(mgr.permissions)
                          ).map((p) => (
                            <Chip key={p} label={p} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* TAB 6 — REVENUE */}
        <TabPanel value={tabValue} index={5}>
          {revenueLoading ? (
            <CircularProgress color="secondary" />
          ) : revenue ? (
            <Grid container spacing={2}>
              {[
                { label: "Total Revenue", value: revenue.total_revenue ? `₹${Number(revenue.total_revenue).toLocaleString("en-IN")}` : "-" },
                { label: "Total Bookings", value: revenue.total_bookings ?? "-" },
                { label: "Confirmed", value: revenue.confirmed ?? "-" },
                { label: "Cancelled", value: revenue.cancelled ?? "-" },
                { label: "Check-ins", value: revenue.check_ins ?? "-" },
              ].map(({ label, value }) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <Card sx={{ backgroundColor: theme.palette.background.alt }}>
                    <CardContent>
                      <Typography variant="h6" color={theme.palette.secondary[300]}>
                        {label}
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" color={theme.palette.secondary[100]}>
                        {value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color={theme.palette.secondary[300]}>No revenue data.</Typography>
          )}
        </TabPanel>
      </Box>

      {editOpen && (
        <FacilityForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          facility={facility}
          onSuccess={fetchFacility}
        />
      )}

      <ActivityDialog
        open={activityDialogOpen}
        onClose={() => setActivityDialogOpen(false)}
        facilityId={id}
        activity={selectedActivity}
        onSuccess={fetchActivities}
      />
    </>
  );
};

export default FacilityDetail;
